/**
 * YINSH board geometry — verbatim port of the canonical `renderBoard()`
 * function in the design-handoff HTML.
 *
 * Source of truth:
 *   docs/task-log/20260428-design_handoff_board_games/games-design-system.html
 *   (lines 2007–2307, the IIFE `buildYinsh`)
 *
 * Canonical positions are uppercase ("E5"); the engine uses lowercase ("e5").
 * Adapters at the bottom convert between the two.
 *
 * All values below — POINTS, SEGMENTS, EMPTY_POINTS, dimensions — are computed
 * once at module load. The React Board component reads from these constants.
 */

export const COLS: readonly string[] = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
];

/** Column heights from A..K — 4,7,8,9,10,9,10,9,8,7,4. */
export const HEIGHTS: readonly number[] = [4, 7, 8, 9, 10, 9, 10, 9, 8, 7, 4];

/** Row at which each column starts — 2,1,1,1,1,2,2,3,4,5,7. */
export const STARTS: readonly number[] = [2, 1, 1, 1, 1, 2, 2, 3, 4, 5, 7];

/** Lattice edge length (board units). */
export const SP = 30;

/** Cosine of 30 degrees — precomputed once. */
export const COS30 = Math.sqrt(3) / 2;

/** Padding around bounding box in board units. */
export const PAD = 52;

/** Gap (board units) left on each side so neighbouring hit circles don't touch. */
const HIT_CLEARANCE = 2;

/**
 * Radius of the invisible click target at each intersection (board units).
 * Adjacent intersections are exactly SP apart, so this must stay below SP/2
 * for the hit circles to tile without overlapping — otherwise the
 * topmost-painted circle wins a tap instead of the nearest centre. Canonical
 * value is SP/2 − HIT_CLEARANCE, matching the wireframe renderer.
 */
export const HIT_RADIUS = SP / 2 - HIT_CLEARANCE;

export interface Point {
  readonly col: number;
  readonly row: number;
  /** Pre-offset x (raw projection, no PAD). */
  readonly x: number;
  /** Pre-offset y (raw projection, no PAD). */
  readonly y: number;
}

export interface Segment {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

export interface EmptyPoint {
  /** Engine-style lowercase id, e.g. "e5". */
  readonly id: string;
  /** Final cx in viewBox space (raw + OX). */
  readonly cx: number;
  /** Final cy in viewBox space (raw + OY). */
  readonly cy: number;
}

/** Project a (colIdx, rowNum) pair to raw (x, y). Identical to canonical. */
export function pointXY(
  colIdx: number,
  rowNum: number,
): { x: number; y: number } {
  const x = colIdx * SP - rowNum * SP * 0.5;
  const y = -rowNum * SP * COS30;
  return { x, y };
}

/* ----- Build POINTS once at module load ----- */
const _points: Record<string, Point> = {};
for (let c = 0; c < 11; c++) {
  for (let r = STARTS[c]; r < STARTS[c] + HEIGHTS[c]; r++) {
    const id = COLS[c] + r;
    const { x, y } = pointXY(c, r);
    _points[id] = { col: c, row: r, x, y };
  }
}

/** Map of canonical-uppercase position id → Point. */
export const POINTS: Readonly<Record<string, Point>> = _points;

/* ----- Bounds ----- */
const _xs = Object.values(_points).map((p) => p.x);
const _ys = Object.values(_points).map((p) => p.y);
export const MIN_X = Math.min(..._xs);
export const MAX_X = Math.max(..._xs);
export const MIN_Y = Math.min(..._ys);
export const MAX_Y = Math.max(..._ys);
export const W = MAX_X - MIN_X + PAD * 2;
export const H = MAX_Y - MIN_Y + PAD * 2;
export const OX = -MIN_X + PAD;
export const OY = -MIN_Y + PAD;

/* ----- Build SEGMENTS once using 3-direction algorithm ----- */
const DIRS: readonly { dc: number; dr: number }[] = [
  { dc: 1, dr: 0 },
  { dc: 0, dr: 1 },
  { dc: 1, dr: 1 },
];

const _segments: Segment[] = [];
for (const p of Object.values(_points)) {
  for (const d of DIRS) {
    const prevC = p.col - d.dc;
    const prevR = p.row - d.dr;
    const prevId = prevC >= 0 && prevC < 11 ? COLS[prevC] + prevR : null;
    if (prevId && _points[prevId]) continue;
    let last: Point = p;
    while (true) {
      const nc = last.col + d.dc;
      const nr = last.row + d.dr;
      if (nc < 0 || nc >= 11) break;
      const nid = COLS[nc] + nr;
      const next = _points[nid];
      if (!next) break;
      last = next;
    }
    if (last !== p) {
      _segments.push({
        x1: p.x + OX,
        y1: p.y + OY,
        x2: last.x + OX,
        y2: last.y + OY,
      });
    }
  }
}

/** Lattice line segments — coordinates already include OX/OY. */
export const SEGMENTS: ReadonlyArray<Segment> = _segments;

/* ----- Build EMPTY_POINTS once (engine-id, offset coords) ----- */
const _emptyPoints: EmptyPoint[] = [];
for (let c = 0; c < 11; c++) {
  for (let r = STARTS[c]; r < STARTS[c] + HEIGHTS[c]; r++) {
    const canonicalId = COLS[c] + r;
    const p = _points[canonicalId];
    _emptyPoints.push({
      id: canonicalToEngine(canonicalId),
      cx: p.x + OX,
      cy: p.y + OY,
    });
  }
}

/** All 85 valid points, keyed by engine-style lowercase id, with offset coords. */
export const EMPTY_POINTS: ReadonlyArray<EmptyPoint> = _emptyPoints;

/* ----- Position case adapters ----- */

/** Convert engine pos ("e5") to canonical pos ("E5"). */
export function engineToCanonical(pos: string): string {
  if (pos.length < 2) return pos;
  return pos[0].toUpperCase() + pos.slice(1);
}

/** Convert canonical pos ("E5") to engine pos ("e5"). */
export function canonicalToEngine(pos: string): string {
  if (pos.length < 2) return pos;
  return pos[0].toLowerCase() + pos.slice(1);
}

/**
 * Look up a point by either engine ("e5") or canonical ("E5") id and return
 * a point with offset coords (cx, cy). Returns undefined for unknown ids.
 */
export function getPoint(
  pos: string,
): { col: number; row: number; cx: number; cy: number } | undefined {
  const canonical = engineToCanonical(pos);
  const p = _points[canonical];
  if (!p) return undefined;
  return { col: p.col, row: p.row, cx: p.x + OX, cy: p.y + OY };
}
